<template>
  <base-draggable-modal
      :name="modal_name"
      max-height="80vh"
      width="50vw"
      :fullscreen="$vuetify.display.xsOnly"
      :ref="modal_name">
    <v-card rounded="xl">
      <base-modal>
        <template #header>
          <v-toolbar class="draggable_selector" flat color="secondary" density="compact">
            <v-toolbar-title v-if="label" class="text-primary">{{ label }}</v-toolbar-title>
            <v-toolbar-title v-else class="text-primary">{{ $store.getters.translate('create') }} {{ $store.getters.translate(page.model) }}</v-toolbar-title>
          </v-toolbar>
        </template>
        <template #content>
            <v-card-text>
              <!--USUAL FIELDS-->
              <base-field
                  v-for="field in usual_fields"
                  :key="field.name"
                  :modelValue="record[field.name]"
                  :field="field"
                  @change="usualFieldDataChanged"/>
              <!--UPLOAD FILES-->
              <v-row v-if="files_required" class="mt-3">
                <v-col v-cloak @drop.prevent="addDropFile" @dragover.prevent>
                  <v-file-input
                      v-model="new_files"
                      :multiple="multiple_files"
                      :label="$store.getters.translate('files')"
                      placeholder="Drop file to upload"
                      color="primary"
                      bg-color="white"
                      density="compact"
                      hide-details
                      show-size/>
                </v-col>
              </v-row>
              <v-row v-if="addresses_required" class="mt-0">
                <v-col>
                  <base-addresses v-model="record.addresses" address_type="visiting" label="contact_address" @update="addressesChanged"/>
                </v-col>
              </v-row>
              <v-row v-if="email_addresses_required" class="mt-0">
                <v-col>
                  <base-email-addresses v-model="record.emailaddresses" email_type="personal" label="emailaddress" @update="emailsChanged"/>
                </v-col>
              </v-row>
              <v-row v-if="attach_to_required" class="mt-0">
                <v-col>
                  <v-autocomplete
                      density="default"
                      class="reg-normal text-primary mt-3"
                      base-color="border-tertiary"
                      bg-color="secondary"
                      color="primary"
                      v-model="attach_to_selected_models"
                      :items="['Candidates', 'Exams', 'Dossiers']"
                      :return-object="false"
                      hide-details
                      clearable
                      multiple>
                    <template #label>
                      <span class="sm-normal text-subtle">{{ $store.getters.translate('attach_to') }}</span>
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                      v-if="attach_to_selected_models.includes('Candidates')"
                      density="default"
                      class="reg-normal text-primary mt-3"
                      base-color="border-tertiary"
                      bg-color="secondary"
                      color="primary"
                      v-model="record.candidate_ids"
                      :items="candidates"
                      :return-object="false"
                      item-value="id"
                      item-title="name"
                      hide-details
                      clearable
                      multiple>
                    <template #label>
                      <span class="sm-normal text-subtle">{{ $store.getters.translate('candidates') }}</span>
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                      v-if="attach_to_selected_models.includes('Exams')"
                      density="default"
                      class="reg-normal text-primary mt-3"
                      base-color="border-tertiary"
                      bg-color="secondary"
                      color="primary"
                      v-model="record.exam_ids"
                      :items="exams"
                      :return-object="false"
                      item-value="id"
                      item-title="name"
                      hide-details
                      clearable
                      multiple>
                    <template #label>
                      <span class="sm-normal text-subtle">{{ $store.getters.translate('exams') }}</span>
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                      v-if="attach_to_selected_models.includes('Dossiers')"
                      density="default"
                      class="reg-normal text-primary mt-3"
                      base-color="border-tertiary"
                      bg-color="secondary"
                      color="primary"
                      v-model="record.dossier_ids"
                      :items="dossiers"
                      :return-object="false"
                      item-value="id"
                      item-title="name"
                      hide-details
                      clearable
                      multiple>
                    <template #label>
                      <span class="sm-normal text-subtle">{{ $store.getters.translate('dossiers') }}</span>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <!--TAGS-->
              <base-tags v-if="tags_required" :modelValue="record.tags" :modal_name="page.name" :label="$store.getters.translate('tags')" @change="tagsChanged"/>
            </v-card-text>
        </template>
        <template #footer>
          <v-card-text>
            <v-row>
              <v-col>
                <base-action-button :width="'100%'" @action="reset">
                  <template #prepend><PhX style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("close") }}
                </base-action-button>
              </v-col>
              <v-col>
                <base-action-button :background="true" :width="'100%'" @action="save">
                  <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("save") }}
                </base-action-button>
              </v-col>
            </v-row>
          </v-card-text>
        </template>
      </base-modal>
    </v-card>
  </base-draggable-modal>
</template>

<script>
import {useAbility} from "@casl/vue";
import helpFunctions from "../../plugins/helpFunctions";
import BaseModal from "../../components/commonComponents/BaseModal";
import BaseDraggableModal from "../../components/commonComponents/BaseDraggableModal";
import BaseTags from "./BaseTags";
import BaseAddresses from "./BaseAddresses";
import BaseEmailAddresses from "./BaseEmailAddresses";
import BaseActionButton from "./BaseActionButton";
import {PhFloppyDisk, PhX} from "@phosphor-icons/vue";

export default {
  props: ["modal_name", "page", "label", "addresses_required", "email_addresses_required", "files_required", "multiple_files", "attach_to_required"],
  components: {
    BaseEmailAddresses,
    BaseModal,
    BaseDraggableModal,
    BaseTags,
    BaseAddresses,
    BaseActionButton,
    PhFloppyDisk, PhX,
  },
  setup() {
    const { can } = new useAbility();
    return { can }
  },
  data() {
    return {
      record: {},
      usual_fields: [],
      tags_required: false,
      //FILES VARIABLE
      new_files: [],
      //NOTES VARIABLES
      attach_to_selected_models: [],
      candidates: [],
      exams: [],
      dossiers: [],
    };
  },
  methods: {
    ...helpFunctions.modal_functions,
    async load() {
      this.record = {};
      this.openModal(this.modal_name);
      await this.getFields();
    },
    async save() {
      if (helpFunctions.modelFieldsFilled(this.$toast, this.record, this.usual_fields)) {
        if(this.page.model === 'translation') {
          let new_key = this.$lodash.lowerCase(this.record.key);

          this.record.key = new_key.replaceAll('(', "_").replaceAll(')', "_").replaceAll('[', "_").replaceAll(']', "_")
              .replaceAll('-', "_").replaceAll('/', "_").replaceAll('\\', "_").replace(/ /g, "_");
        }
        let record_id = await helpFunctions.createModel(this.$toast, this.page.name, this.record);
        if(record_id) {
          if(this.multiple_files && this.new_files.length > 0) {
            await this.uploadFiles(this.new_files, record_id);
          }
          else if(typeof this.new_files === "object" && this.new_files.name) {
            await this.uploadOneFile(this.new_files, record_id);
          }
          if(this.page.model === 'translation') {
            await this.$store.dispatch("refreshTranslations");
          }
          this.$emit('refresh', record_id);
          this.reset();
        }
      }
    },
    reset() {
      this.record = {};
      this.new_files = [];
      this.closeModal(this.modal_name);
    },
    usualFieldDataChanged(data) {
      if(this.record[data[0]] !== data[1]) {
        this.record[data[0]] = data[1];
      }
    },
    emailsChanged(updated_emails) {
      this.record.emailaddresses = updated_emails;
    },
    addressesChanged(updated_addresses) {
      this.record.addresses = updated_addresses;
    },
    tagsChanged(data) {
      this.record.tags = data;
    },
    addDropFile(e) {
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        this.new_files.push(e.dataTransfer.files[i]);
      }
    },
    async uploadFiles(files, record_id) {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("attachment[]", files[i]);
      }
      formData.append("id", record_id);
      await helpFunctions.saveFile(this.$toast, this.page.name, formData);
    },
    async uploadOneFile(file, record_id) {
      const formData = new FormData();
      formData.append("attachment", file);
      formData.append("id", record_id);
      await helpFunctions.saveFile(this.$toast, this.page.name, formData);
    },
    async getFields() {
      const response = await helpFunctions.getFields(this.page.model, "show_on_creation");
      this.tags_required = response.tags_required;
      this.usual_fields = response.usual_fields;
    },
  },
};
</script>